/**
 * Chat store — internal state + subscribe/emit/setState plumbing.
 *
 * The mutable closure (`state`, `inputValue`, `switchGeneration`) MUST stay in
 * a single module so every action mutates the same reference and every
 * subscriber observes the same updates. Actions, summarize polling, and the
 * public hooks all import from here; the slim `chatStore.ts` re-exports the
 * pieces consumers historically imported.
 *
 * `useSyncExternalStore` contract: `getState` must return a referentially
 * stable snapshot when the underlying data hasn't changed. Every mutation
 * here rebuilds the affected slice via `{ ...state, ... }` and emits, so
 * memoised selectors stay stable across no-op subscribers.
 */
import type { ChatState } from './chatStore.types.js';
import { STREAM_NOTIFY_INTERVAL_MS } from './uiConstants';

// ─── Main state ───────────────────────────────────────────────────────────────

type Listener = () => void;
const listeners = new Set<Listener>();

let state: ChatState = {
  conversationId: crypto.randomUUID(),
  messages: [],
  earlier: null,
  isStreaming: false,
  totalCost: 0,
  provider: 'subscription',
  // Empty until hydrateDefaults() adopts the server's Settings-resolved
  // default — the renderer never assumes which agent runs.
  model: '',
  useExtendedContext: false,
  routeReason: '',
  routeEffort: '',
  conversations: [],
  conversationsLoaded: false,
  abortController: null,
  summarizeJob: null,
  pendingAttachments: [],
  uploadError: null,
  historyOpen: false,
  conversationMeta: {},
  explicitSelection: false,
  turnMeta: null,
  pendingQuestion: null,
};

// Monotonic switch counter — any conversation-identity change bumps it, so a
// slow fetch from a superseded switch can never apply stale data to the view.
let switchGeneration = 0;

// ─── Throttled notification for streamed content ───────────────────────────────
// useSyncExternalStore renders every notification as a synchronous commit. A
// reply's events often arrive many to a network chunk and are applied in one
// run of microtasks. A commit per event there counts as nested updates whenever
// lower-priority work is waiting (e.g. a Radix menu item's effect), and React
// throws #185 "Maximum update depth exceeded" past 50 of them. Content
// updates applied through `withThrottledNotify` change the state at once but
// notify on a timer, so each render is its own task. Every other update still
// notifies at once — and with it any content waiting — so a status or error
// never renders beside stale content (the same split as the AI SDK's useChat
// `throttle`).

let throttleDepth = 0;
let notifyTimer: ReturnType<typeof setTimeout> | null = null;

function emit(): void {
  if (notifyTimer !== null) {
    clearTimeout(notifyTimer);
    notifyTimer = null;
  }
  for (const fn of listeners) fn();
}

/** Apply `update` now; subscribers hear of it within STREAM_NOTIFY_INTERVAL_MS. */
export function withThrottledNotify(update: () => void): void {
  throttleDepth++;
  try {
    update();
  } finally {
    throttleDepth--;
  }
}

export function getState(): ChatState {
  return state;
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Replace `state` with a new reference and notify — at once, or within
 * STREAM_NOTIFY_INTERVAL_MS inside `withThrottledNotify`. Actions go through
 * this helper instead of poking the closure variable directly so that the place
 * where snapshots are minted (and emitted) is exactly one line.
 */
export function setState(next: ChatState): void {
  state = next;
  if (throttleDepth > 0) {
    notifyTimer ??= setTimeout(emit, STREAM_NOTIFY_INTERVAL_MS);
    return;
  }
  emit();
}

/** Like setState but skips the emit — needed when an action is about to
 *  follow with another setState (avoids double-render) or when the caller
 *  intentionally defers notification (e.g. setting an AbortController and
 *  returning it without bouncing subscribers yet). */
export function setStateSilent(next: ChatState): void {
  state = next;
}

export function bumpSwitchGeneration(): number {
  return ++switchGeneration;
}

export function getSwitchGeneration(): number {
  return switchGeneration;
}

// ─── Separate high-frequency input state ─────────────────────────────────────
// The composer's keystroke stream is kept in its own store so a typing user
// doesn't re-render the whole chat view per character.

let inputValue = '';
const inputListeners = new Set<() => void>();

export function subscribeInput(listener: () => void): () => void {
  inputListeners.add(listener);
  return () => {
    inputListeners.delete(listener);
  };
}

export function getInputSnapshot(): string {
  return inputValue;
}

export function getInputValue(): string {
  return inputValue;
}

export function setInputValue(value: string): boolean {
  if (inputValue === value) return false;
  inputValue = value;
  for (const l of inputListeners) l();
  return true;
}

// ─── Selectors (memoised for referential stability) ──────────────────────────

export function createSelector<T>(select: (s: ChatState) => T): () => T {
  let prev: T | undefined;
  return () => {
    const next = select(getState());
    if (prev !== next) prev = next;
    return prev as T;
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function makeId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function appendTextToSegments(
  segments: import('./chatStore.types.js').StreamSegment[],
  text: string,
): import('./chatStore.types.js').StreamSegment[] {
  const last = segments[segments.length - 1];
  if (last && last.type === 'text') {
    return [...segments.slice(0, -1), { type: 'text', content: last.content + text }];
  }
  return [...segments, { type: 'text', content: text }];
}
